import React, { useCallback, type ComponentProps } from 'react';
import styled, { css } from 'styled-components';
import CollapsiblePanel, {
    type CollapsiblePanelChangeHandler,
} from '@splunk/react-ui/CollapsiblePanel';
import { variables } from '@splunk/themes';
import { useSidebarContext } from '@splunk/dashboard-context';
import { customThemeVariables } from './customThemeVariables';

export const isPadded = css`
    padding: ${variables.spacingLarge};
`;

const StyledContainer = styled.div<{ padded: boolean }>`
    background-color: ${customThemeVariables.sidebarPanelBackgroundColor};
    ${(props) => (props.padded ? isPadded : '')}
`;

/** SUI Panel props, less what UDF overrides and the change handlers (bug in 4.28 types and unions with `never`) */
type CollapsiblePanelProps = Omit<
    ComponentProps<typeof CollapsiblePanel>,
    'onChange' | 'onRequestOpen' | 'onRequestClose' | 'open'
>;

export type SidebarCollapsiblePanelProps = CollapsiblePanelProps & {
    padded?: boolean;
    panelId: string;
};

export const SidebarCollapsiblePanel = ({
    children,
    padded = true,
    panelId,
    defaultOpen,
    ...rest
}: SidebarCollapsiblePanelProps) => {
    const {
        collapsibleState,
        filteredCollapsibleState,
        onCollapsibleOpen,
        onCollapsibleClose,
    } = useSidebarContext() ?? {};

    const openState =
        filteredCollapsibleState?.[panelId] ?? collapsibleState?.[panelId];
    // if this is the initial sidebar load, use the defaultOpen value
    //   if there's already saved collapsible state, use that
    const isOpen = openState === undefined ? !!defaultOpen : openState;

    const handleChange = useCallback<CollapsiblePanelChangeHandler>(
        (_event, { action }) => {
            if (action === 'close') {
                onCollapsibleClose?.(panelId);
            } else {
                onCollapsibleOpen?.(panelId);
            }
        },
        [panelId, onCollapsibleOpen, onCollapsibleClose]
    );

    return (
        <CollapsiblePanel
            inset={false}
            {...rest}
            panelId={panelId}
            open={isOpen}
            onChange={handleChange}
            data-test-open={isOpen}
            headingLevel={3}
        >
            <StyledContainer padded={padded}>{children}</StyledContainer>
        </CollapsiblePanel>
    );
};
